<template>
  <div class="dependence-chart-card">
    <div class="card-header">
      <div class="header-content">
        <h3 class="card-title">
          <i class="fas fa-chart-line title-icon"></i>
          Dependência
        </h3>
        <p class="card-subtitle">Total por Grau de Dependência</p>
      </div>
    </div>

    <div class="card-content">
      <ErrorsMessages :errors="errors" />

      <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
        <div class="degrees-list">
          <div
            v-for="degree in degrees"
            :key="degree.id"
            class="degree-section"
          >
            <!-- Degree Header -->
            <div class="degree-header">
              <div class="degree-info">
                <div class="degree-icon">
                  <i :class="getDegreeIcon(degree.name)"></i>
                </div>
                <div class="degree-details">
                  <h4 class="degree-name">{{ degree.name.toUpperCase() }}</h4>
                  <span class="degree-total"
                    >{{ getDegreeTotal(degree) }} residentes</span
                  >
                </div>
              </div>
              <div
                class="degree-badge"
                :class="getDegreeBadgeClass(degree.name)"
              >
                {{ getDegreeLevel(degree.name) }}
              </div>
            </div>

            <!-- Gender Breakdown -->
            <div class="genders-breakdown">
              <div
                v-for="gender in degree.genders"
                :key="gender.id"
                class="gender-row"
                :class="`gender-${gender.name.toLowerCase()}`"
              >
                <div class="gender-info">
                  <div class="gender-icon-small">
                    <i :class="getGenderIcon(gender.name)"></i>
                  </div>
                  <span class="gender-label">{{
                    gender.name.toUpperCase()
                  }}</span>
                </div>

                <div class="gender-stats">
                  <div class="gender-progress-container">
                    <div
                      class="gender-progress-bar"
                      :class="`progress-${gender.name.toLowerCase()}`"
                      :style="{
                        width: getGenderPercentage(gender.amount, degree) + '%',
                      }"
                    ></div>
                  </div>
                  <span class="gender-count">{{ gender.amount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Overall Summary -->
        <div class="dependence-summary">
          <div class="summary-header">
            <h4 class="summary-title">
              <i class="fas fa-chart-pie mr-2"></i>
              Distribuição Geral
            </h4>
          </div>
          <div class="summary-content">
            <div class="summary-stats">
              <div class="summary-item">
                <span class="summary-label">Total de Residentes</span>
                <span class="summary-value">{{ totalResidents }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Graus de Dependência</span>
                <span class="summary-value">{{ degrees.length }}</span>
              </div>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";

export default {
  name: "DegreeOfDependence",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      degrees: [],
      errors: [],
      loading: false,
    };
  },
  computed: {
    totalResidents() {
      return this.degrees.reduce((total, degree) => {
        return (
          total + degree.genders.reduce((sum, gender) => sum + gender.amount, 0)
        );
      }, 0);
    },
  },
  created() {
    this.retrieve();
  },
  methods: {
    getDegreeIcon(degreeName) {
      const name = degreeName.toLowerCase();
      if (name.includes("grau1") || name.includes("grau 1"))
        return "fas fa-battery-quarter";
      if (name.includes("grau2") || name.includes("grau 2"))
        return "fas fa-battery-half";
      if (name.includes("grau3") || name.includes("grau 3"))
        return "fas fa-battery-full";
      return "fas fa-chart-line";
    },
    getDegreeLevel(degreeName) {
      const name = degreeName.toLowerCase();
      if (name.includes("grau1") || name.includes("grau 1")) return "Baixo";
      if (name.includes("grau2") || name.includes("grau 2")) return "Médio";
      if (name.includes("grau3") || name.includes("grau 3")) return "Alto";
      return "N/A";
    },
    getDegreeBadgeClass(degreeName) {
      const name = degreeName.toLowerCase();
      if (name.includes("grau1") || name.includes("grau 1")) return "badge-low";
      if (name.includes("grau2") || name.includes("grau 2"))
        return "badge-medium";
      if (name.includes("grau3") || name.includes("grau 3"))
        return "badge-high";
      return "badge-default";
    },
    getGenderIcon(genderName) {
      const name = genderName.toLowerCase();
      if (name === "feminino") return "fas fa-venus";
      if (name === "masculino") return "fas fa-mars";
      return "fas fa-user";
    },
    getDegreeTotal(degree) {
      return degree.genders.reduce((sum, gender) => sum + gender.amount, 0);
    },
    getGenderPercentage(amount, degree) {
      const total = this.getDegreeTotal(degree);
      if (total === 0) return 0;
      return Math.round((amount / total) * 100);
    },
    async retrieve() {
      try {
        this.loading = true;

        const response = await api.get(
          "/rest-home/graphics/degree-of-dependence"
        );

        const { degrees, error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.degrees = degrees;
          this.errors = [];
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.dependence-chart-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  height: fit-content;
}

.card-header {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  padding: 20px;
  border-bottom: 1px solid #34d399;
}

.header-content {
  text-align: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #065f46;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  color: #059669;
  margin-right: 8px;
  font-size: 16px;
}

.card-subtitle {
  color: #065f46;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.card-content {
  padding: 24px;
}

.loading-overlay {
  border-radius: 12px;
  min-height: 200px;
}

/* Degrees List */
.degrees-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.degree-section {
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s ease;
}

.degree-section:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Degree Header */
.degree-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.degree-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.degree-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
}

.degree-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.degree-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.degree-total {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* Degree Badges */
.degree-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-low {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.badge-medium {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  border: 1px solid #f59e0b;
}

.badge-high {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.badge-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid #cbd5e1;
}

/* Gender Breakdown */
.genders-breakdown {
  padding: 16px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gender-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.gender-row:hover {
  border-color: #cbd5e1;
  transform: translateX(4px);
}

.gender-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gender-icon-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
}

.gender-feminino .gender-icon-small {
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
}

.gender-masculino .gender-icon-small {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.gender-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.gender-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gender-progress-container {
  width: 60px;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.gender-progress-bar {
  height: 100%;
  transition: width 0.8s ease;
  border-radius: 3px;
}

.progress-feminino {
  background: linear-gradient(90deg, #ec4899 0%, #db2777 100%);
}

.progress-masculino {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.gender-count {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  min-width: 20px;
  text-align: right;
}

/* Summary */
.dependence-summary {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}

.summary-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.summary-title i {
  color: #0f65b0;
}

.summary-content {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.summary-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-content {
    padding: 16px;
  }

  .card-header {
    padding: 16px;
  }

  .degree-header {
    padding: 16px;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .degree-info {
    justify-content: center;
  }

  .genders-breakdown {
    padding: 12px 16px 16px 16px;
  }

  .gender-row {
    padding: 10px 12px;
  }

  .gender-icon-small {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .dependence-summary {
    padding: 16px;
  }

  .summary-content {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .card-title {
    font-size: 16px;
    flex-direction: column;
    gap: 4px;
  }

  .card-subtitle {
    font-size: 13px;
  }

  .degree-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .degree-name {
    font-size: 14px;
  }

  .gender-row {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .gender-stats {
    justify-content: center;
  }

  .summary-item {
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .summary-value {
    font-size: 16px;
  }
}
</style>